/*
 * Copyright (C) 2018 Spanning Cloud Apps. LLC  All rights reserved.
 */
import { ValidationUtils } from 'spanalog/lib/utils/ValidationUtils';
import './wizardButton.tpl.html';

/*
 * This directive displays a custom button in the wizard buttons directive.
 */

/**
 * Defines the sbsf-wizard-button directive.
 *
 * @returns {Object} - The directive settings
 */
export function directive() {
  return {
    controller: 'WizardButtonCtrl',
    templateUrl: 'shared/wizard/wizardButton.tpl.html',
    restrict: 'E',
    scope: {
      // The WizardButton.
      button: '=',
      // The wizard controller
      wizard: '='
    }
  };
}

controller.$inject = ['$scope'];

/**
 * The controller for the directive.
 */
export function controller($scope) {
  ValidationUtils.assertIsObject($scope.button, 'button');
  ValidationUtils.assertIsObject($scope.wizard, 'wizard');

  // Publish functions in the scope
  $scope.click = click;
  $scope.getText = getText;
  $scope.getTooltip = getTooltip;
  $scope.isEnabled = isEnabled;
  $scope.isVisible = isVisible;

  /**
   * Clicks the button.
   *
   * @returns {Promise} a promise that is resolved when the operation completes.
   */
  function click() {
    return $scope.button.click().then(function (result) {
      // click returns a promise that is resolved with one of the following:
      // 1) true if the wizard can be dismissed
      // 2) false if the wizard should remain
      // 3) an object containing the parameters to pass to the wizard cancel function to display an error message.
      if (result === true) {
        return $scope.wizard.cancel({ showConfirm: false });
      } else if (_.isObject(result)) {
        return $scope.wizard.cancel(result);
      } else if (result !== false) {
        throw new Error('Unexpected result returned from button click: ' + JSON.stringify(result));
      }
    });
  }

  /**
   * Returns the text label for the button.
   *
   * @returns {string} the text label to be shown.
   */
  function getText() {
    return $scope.button.getText();
  }

  /**
   * Returns the tooltip for the button.
   *
   * @returns {string} the tooltip to be shown.
   */
  function getTooltip() {
    return $scope.button.getTooltip();
  }

  /**
   * Determines whether the button should be enabled.
   *
   * @returns {boolean} true if the button should be enabled, false otherwise.
   */
  function isEnabled() {
    return $scope.button.getEnabled();
  }

  /**
   * Determines whether the button should be visible.
   *
   * @returns {boolean} true if the button should be visible, false otherwise.
   */
  function isVisible() {
    return $scope.button.getVisible();
  }
}
